import Input from "@/components/input/Input";
import Loading from "@/components/loading/Loading";
import { LoadingContainerStyled } from "@/components/loading/LoadingContainer";
import SecondaryText from "@/components/SecondaryText";
import useInputDebounce from "@/hooks/useInputDebounce";
import { Link } from "react-router";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { menuData } from "./MenuData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SubItem {
  id: string;
  title: string;
  path: string;
  relatedWords?: string[];
}

interface Group {
  id: string;
  title: string;
  subItems: SubItem[];
}

function SearchInput() {
  const { debouncedValue, handleInputChange, inputValue, isSearching } =
    useInputDebounce();

  const [resultsData, setResultsData] = useState<Group[]>([]);
  const [selectedIndex, setSelectedIndex] = useState({
    group: -1,
    subItem: -1,
  }); // Estado para el índice seleccionado
  const [isOpen, setIsOpen] = useState(false);
  const resultsContainerRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleArrowKeyPress = (e: KeyboardEvent) => {
      const { group, subItem } = selectedIndex;
      const groupsCount = resultsData.length;

      if (e.key === "ArrowDown") {
        if (group >= 0 && subItem < resultsData[group].subItems.length - 1) {
          const newState = {
            ...selectedIndex,
            subItem: selectedIndex.subItem + 1,
          };

          setSelectedIndex(newState);

          // Quitar el foco del campo de entrada
          inputRef.current?.blur();

          // Poner el foco en el elemento seleccionado
          const selectedElement = document.getElementById(
            `groupItem-${newState.group}-${newState.subItem}`
          );
          if (selectedElement) {
            selectedElement.focus();
          }
        } else if (group < groupsCount - 1) {
          const newState = {
            group: selectedIndex.group + 1,
            subItem: 0,
          };

          setSelectedIndex(newState);

          // Quitar el foco del campo de entrada
          inputRef.current?.blur();

          // Poner el foco en el elemento seleccionado
          const selectedElement = document.getElementById(
            `groupItem-${newState.group}-${newState.subItem}`
          );
          if (selectedElement) {
            selectedElement.focus();
          }
        }
      } else if (e.key === "ArrowUp") {
        if (group >= 0 && subItem > 0) {
          setSelectedIndex((prevState) => ({
            ...prevState,
            subItem: prevState.subItem - 1,
          }));
        } else if (group > 0) {
          setSelectedIndex((prevState) => ({
            group: prevState.group - 1,
            subItem: resultsData[prevState.group - 1].subItems.length - 1,
          }));
        }
      }
    };

    document.addEventListener("keydown", handleArrowKeyPress);

    return () => {
      document.removeEventListener("keydown", handleArrowKeyPress);
    };
  }, [selectedIndex, resultsData]);

  interface MenuItem {
    id: string;
    title: string;
    subItems: SubItem[];
  }

  function buscarEnItemsPorPalabras(menuData: MenuItem[], query: string): void {
    const palabras: string[] = query.toLowerCase().match(/\w+/g) || [];

    const resultados: Group[] = [];

    function incluyeTodasPalabras(cadena: string, palabras: string[]): boolean {
      if (!cadena || !palabras) {
        return false;
      }

      return palabras.every((palabra) =>
        cadena.toLowerCase().includes(palabra)
      );
    }

    const gruposCoincidentes: Group[] = menuData.filter((item) =>
      incluyeTodasPalabras(item.title, palabras)
    );
    if (gruposCoincidentes.length > 0) {
      resultados.push(...gruposCoincidentes);
    } else {
      menuData.forEach((item) => {
        const subItemsCoincidentes: SubItem[] = item.subItems.filter(
          (subItem) => {
            return (
              incluyeTodasPalabras(subItem.title, palabras) ||
              (subItem.relatedWords &&
                subItem.relatedWords.some((related) =>
                  incluyeTodasPalabras(related, palabras)
                ))
            );
          }
        );

        if (subItemsCoincidentes.length > 0) {
          resultados.push({
            id: item.id,
            title: item.title,
            subItems: subItemsCoincidentes,
          });
        }
      });
    }

    setResultsData(resultados);
  }

  const onInputChange = (value: string) => {
    handleInputChange(value);

    if (value) {
      buscarEnItemsPorPalabras(menuData, value);
    }
  };

  const onGroupItemClick = () => {
    setSelectedIndex({ group: -1, subItem: -1 });
    handleInputChange("");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Search className="icon cursor-pointer hover:text-brand-primary transition" />
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Buscar</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>¿Qué deseas hacer?</DialogTitle>
          <DialogDescription>
            Busca en el menú de opciones para encontrar lo que necesitas.
          </DialogDescription>
        </DialogHeader>

        <Input
          rounded
          inputType="search"
          type="text"
          placeholder="¿Qué deseas hacer?"
          id="searchVariant"
          onChange={(e) => onInputChange(e.target.value)}
          value={inputValue}
          clearInput={() => onInputChange("")}
          ref={inputRef}
          className="border"
          inputClassname="py-1"
        />

        {isSearching && inputValue && (
          <LoadingContainerStyled>
            <Loading />
          </LoadingContainerStyled>
        )}

        {!isSearching && inputValue && (
          <div
            className="p-2.5 flex flex-col gap-2.5 select-none w-full"
            ref={resultsContainerRef}
          >
            {resultsData.length > 0 &&
              debouncedValue &&
              resultsData.map((group, groupIndex) => (
                <div
                  key={group.id}
                  className="flex flex-col gap-[5px] last:pb-5"
                >
                  <h3 className="text-xs text-text-terciary transition-all uppercase font-medium border-b border-border">
                    {group.title}
                  </h3>
                  {group.subItems.map((item, subIndex) => (
                    <Link
                      to={item.path}
                      onClick={onGroupItemClick}
                      key={item.id}
                      className={`
                        flex items-center gap-2.5 cursor-pointer p-[5px] pl-2.5 text-text-primary transition-all
                        ${
                          groupIndex === selectedIndex.group &&
                          subIndex === selectedIndex.subItem
                            ? "border border-brand-primary"
                            : "border border-transparent"
                        }
                        hover:bg-background-200
                      `}
                      id={`groupItem-${groupIndex}-${subIndex}`}
                      onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                          if (
                            groupIndex === selectedIndex.group &&
                            subIndex === selectedIndex.subItem
                          ) {
                            e.preventDefault();
                            navigate(item.path);
                            onGroupItemClick();
                          }
                        }
                      }}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              ))}

            {resultsData.length === 0 && debouncedValue && (
              <SecondaryText>
                No se encontraron elementos relacionados...
              </SecondaryText>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SearchInput;
